// Brandeis University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Brandeis's Common Data Set 2025-2026 (cds-2025-26.pdf, Office of
//    Institutional Research): C1 applicants, admits and enrollees for Fall
//    2025 with the residency table; C8 test policy for Fall 2027; C21/C22
//    early plans; G1/G5 2026-2027 costs. Quirks: the C21 count block keeps
//    the form's "For the Fall 2024 entering class" heading, read here as the
//    Fall 2025 class this edition reports (as on other 2025-2026 pages); the
//    G1 "Housing Only" first-year cell reads $111,560 (a typo for a figure
//    near $11,560) and is not used, since the food and housing total is
//    given; G5 leaves resident transportation blank, so there is no
//    transportation row.
//  - Brandeis Admissions: Application Process and Deadlines (the first-year
//    table for the cycle whose spring entry is January 2027, so fall 2027
//    entry), Early Decision at Brandeis, Test-Optional Policy, and
//    First-Year Applicants.
//  - College Scorecard, school id 165015 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Brandeis Common Data Set 2025-2026',
        url: 'https://www.brandeis.edu/institutional-research/docs/cds-2025-26.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Brandeis Admissions, Application Process and Deadlines',
        url: 'https://www.brandeis.edu/admissions/apply/application-process/index.html',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Brandeis Admissions, Early Decision at Brandeis',
        url: 'https://www.brandeis.edu/admissions/apply/application-process/early-decision.html',
        read: '2026-10-01',
    },
    tests: {
        label: 'Brandeis Admissions, Test-Optional Policy',
        url: 'https://www.brandeis.edu/admissions/apply/test-optional-policy.html',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Brandeis Admissions, First-Year Applicants',
        url: 'https://www.brandeis.edu/admissions/apply/application-process/first-year.html',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?165015-Brandeis-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'brandeis-university',
    kbId: 'brandeis_university',
    name: 'Brandeis University',
    shortName: 'Brandeis',
    location: 'Waltham, MA',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Brandeis visual identity colors); decoration only.
    branding: {
        primary: '#003478',
        secondary: '#00B3E9',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/brandeis_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 9756,
        admitted: 4430,
        enrolled: 744,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 1858, admitted: 1036 },
            { group: 'Out-of-state', applied: 4322, admitted: 2752 },
            { group: 'International', applied: 3576, admitted: 642 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Brandeis\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together. It does not count early action applicants, so it gives no early action rate.',
        rows: [
            { plan: 'Early Decision I and II', applied: 680, admitted: 242 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 2', decision: 'December 15', binding: true },
            { plan: 'Early Action', deadline: 'November 2', decision: 'February 1', binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'April 1', binding: false },
        ],
        note: 'The CSS Profile and FAFSA are due on the same date as the admission application in each round, and the admission and financial aid decisions arrive together. The application fee is $80. Regular Decision applicants must send senior midyear grades.',
        source: 'deadlines',
        binding: 'Early Decision I and II are binding, and you must complete the Early Decision Agreement. The two rounds differ only in timing. The aid package an Early Decision applicant gets is the same one they would get in Regular Decision, and Brandeis meets 100% of calculated financial need.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Brandeis has been test-optional since 2013. You say on the Common App whether you want your SAT or ACT scores considered, and every applicant is considered for merit scholarships either way. You may report your own scores on the application; admitted students who enroll send official scores before they matriculate. Brandeis does not require the science section of the new ACT.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 73080 },
            { item: 'Required fees (first year)', amount: 1048 },
            { item: 'Food and housing (on campus)', amount: 20260 },
            { item: 'Books and supplies', amount: 1000 },
            { item: 'Other expenses', amount: 1200 },
        ],
        total: 96588,
        note: 'For a first-year student living on campus. The Common Data Set gives no transportation figure for students on campus.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 30606,
            byIncome: [
                { income: '$0 to $30,000', amount: 10028 },
                { income: '$30,001 to $48,000', amount: 12371 },
                { income: '$48,001 to $75,000', amount: 17007 },
                { income: '$75,001 to $110,000', amount: 27870 },
                { income: 'Over $110,000', amount: 50519 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Brandeis\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
